/**
 * Company & Data Source Selector
 * Multi-tenant selector for super admins and data source selector for regular users
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, Database, ChevronDown, Search, Check } from 'lucide-react';
import { useAuthStore } from '@store/authStore';
import { useDataSourceStore } from '@store/dataSourceStore';
import { api } from '@services/api';
import toast from 'react-hot-toast';

interface DataSource {
  id: string;
  name: string;
  type: string;
  sourceType?: 'database' | 'file_upload' | 'api';
  config: {
    database: string;
    collection?: string;
    uploadedFiles?: Array<{
      fileName: string;
      uploadDate: Date;
      recordCount: number;
    }>;
  };
  availableModules?: string[];
  purpose?: string;
  isDefault: boolean;
  isActive: boolean;
}

interface Company {
  _id: string;
  name: string;
  slug: string;
  domain?: string;
  dataSources?: DataSource[];
}

const CompanyDataSourceSelector = () => {
  const user = useAuthStore(state => state.user);
  const isSuperAdmin = user?.roles?.includes('super-admin') || user?.roles?.includes('super_admin');

  const { 
    selectedCompany, 
    selectedDataSources, 
    setSelectedCompany, 
    toggleDataSource,
    setSelectedDataSources 
  } = useDataSourceStore();

  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [dataSourceModalOpen, setDataSourceModalOpen] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState('');

  const companyDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch all companies (super admin only)
  const { data: companiesData } = useQuery({
    queryKey: ['companies-selector'],
    queryFn: async () => {
      const response = await api.get('/api/v1/companies');
      return response.data;
    },
    enabled: isSuperAdmin,
    staleTime: 60000, // Cache for 1 minute
  });

  // Fetch current user's company data sources (non-super-admin)
  const { data: userCompanyData } = useQuery({
    queryKey: ['user-company', user?.companyId],
    queryFn: async () => {
      const response = await api.get(`/api/v1/companies/${user?.companyId}`);
      return response.data;
    },
    enabled: !isSuperAdmin && !!user?.companyId,
    staleTime: 60000,
  });

  const companies: Company[] = companiesData?.data || [];
  const userCompany: Company | null = userCompanyData?.data || null;

  // Filter companies by search term
  const filteredCompanies = companies.filter(
    company =>
      company.name.toLowerCase().includes(companySearchTerm.toLowerCase()) ||
      company.domain?.toLowerCase().includes(companySearchTerm.toLowerCase())
  );

  // Get available data sources
  const availableDataSources: DataSource[] = isSuperAdmin
    ? selectedCompany?.dataSources || []
    : userCompany?.dataSources || [];

  // Auto-select ALL data sources for non-admin users (so they see all company data)
  // Admin users manually select data sources
  useEffect(() => {
    if (availableDataSources.length > 0 && selectedDataSources.length === 0) {
      if (isSuperAdmin) {
        // Admins start with default only
        const defaultDS = availableDataSources.find(ds => ds.isDefault) || availableDataSources[0];
        setSelectedDataSources([defaultDS]);
      } else {
        // Non-admins get ALL active data sources automatically
        const activeDataSources = availableDataSources.filter(ds => ds.isActive);
        setSelectedDataSources(activeDataSources);
      }
    }
  }, [availableDataSources, selectedDataSources.length, setSelectedDataSources, isSuperAdmin]);

  // Initialize with user's company for non-super-admin
  useEffect(() => {
    if (!isSuperAdmin && userCompany && !selectedCompany) {
      setSelectedCompany(userCompany);
    }
  }, [isSuperAdmin, userCompany, selectedCompany, setSelectedCompany]);

  // Close company dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target as Node)) {
        setCompanyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCompanySelect = (company: Company) => {
    setSelectedCompany(company);
    setCompanyDropdownOpen(false);
    setCompanySearchTerm('');

    // Open data source modal if company has multiple data sources
    if (company.dataSources && company.dataSources.length > 0) {
      setDataSourceModalOpen(true);
    } else {
      toast.error('This company has no data sources configured');
    }
  };

  const handleDataSourceToggle = (dataSource: DataSource) => {
    toggleDataSource(dataSource);
    const isSelected = selectedDataSources.find(ds => ds.id === dataSource.id);
    if (isSelected) {
      toast.success(`Removed: ${dataSource.name}`);
    } else {
      toast.success(`Added: ${dataSource.name}`);
    }
  };

  // Calculate combined module coverage
  const combinedModules = [...new Set(selectedDataSources.flatMap(ds => ds.availableModules || []))];
  
  // Get overlapping modules
  const overlappingModules = selectedDataSources.length > 1
    ? (selectedDataSources[0]?.availableModules || []).filter(module =>
        selectedDataSources.every(ds => (ds.availableModules || []).includes(module))
      )
    : [];

  return (
    <div className="flex items-center gap-2">
      {/* Company Selector (Super Admin Only) */}
      {isSuperAdmin && (
        <div className="relative" ref={companyDropdownRef}>
          <button
            onClick={() => setCompanyDropdownOpen(!companyDropdownOpen)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 transition-all ${
              selectedCompany
                ? 'bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-300 dark:border-purple-600 hover:shadow-md'
                : 'bg-white dark:bg-secondary-800 border-secondary-300 dark:border-secondary-600 hover:bg-secondary-50 dark:hover:bg-secondary-700 border-dashed'
            }`}
            title={selectedCompany ? 'Switch company' : 'Select a company to manage'}
          >
            <Building2
              className={`w-4 h-4 ${
                selectedCompany ? 'text-purple-600 dark:text-purple-400' : 'text-secondary-400'
              }`}
            />
            <div className="text-left">
              <div className="text-[10px] font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider leading-tight">
                {selectedCompany ? 'Company' : 'Select Company'}
              </div>
              <div className="text-xs font-semibold text-secondary-900 dark:text-secondary-100 leading-tight">
                {selectedCompany?.name || 'None'}
              </div>
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-secondary-400" />
          </button>

          {companyDropdownOpen && (
            <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-16 sm:top-auto mt-0 sm:mt-2 w-auto sm:w-80 bg-white dark:bg-secondary-800 rounded-lg shadow-xl border border-secondary-200 dark:border-secondary-700 z-[200] animate-scale-in max-h-[calc(100vh-5rem)] sm:max-h-96 overflow-hidden flex flex-col">
              {/* Search Input */}
              <div className="p-3 border-b border-secondary-200 dark:border-secondary-700">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
                  <input
                    type="text"
                    placeholder="Search companies..."
                    value={companySearchTerm}
                    onChange={e => setCompanySearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-secondary-50 dark:bg-secondary-900 border border-secondary-300 dark:border-secondary-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    autoFocus
                  />
                </div>
              </div>

              {/* Company List */}
              <div className="flex-1 overflow-y-auto scrollbar-thin">
                {filteredCompanies.length === 0 ? (
                  <div className="p-4 text-center text-secondary-500 text-sm">
                    No companies found
                  </div>
                ) : (
                  filteredCompanies.map(company => (
                    <button
                      key={company._id}
                      onClick={() => handleCompanySelect(company)}
                      className={`w-full px-4 py-3 flex items-center justify-between hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors ${
                        selectedCompany?._id === company._id
                          ? 'bg-primary-50 dark:bg-primary-900/20'
                          : ''
                      }`}
                    >
                      <div className="text-left">
                        <div className="font-medium text-secondary-900 dark:text-white text-sm">
                          {company.name}
                        </div>
                        {company.domain && (
                          <div className="text-xs text-secondary-500 mt-0.5">{company.domain}</div>
                        )}
                      </div>
                      {selectedCompany?._id === company._id && (
                        <Check className="w-4 h-4 text-primary-600" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Data Source Selector - Multi-Select */}
      {selectedCompany && availableDataSources.length > 0 && (
        <button
          onClick={() => setDataSourceModalOpen(true)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 transition-all shadow-sm hover:shadow-md ${
            selectedDataSources.length > 0
              ? 'bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 border-primary-300 dark:border-primary-600'
              : 'bg-white dark:bg-secondary-800 border-secondary-300 dark:border-secondary-600 border-dashed'
          }`}
          title={selectedDataSources.length > 0 ? 'Manage data sources' : 'Select data sources'}
        >
          <Database
            className={`w-4 h-4 ${
              selectedDataSources.length > 0 ? 'text-primary-600 dark:text-primary-400' : 'text-secondary-400'
            }`}
          />
          <div className="text-left">
            <div className="text-[10px] font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider leading-tight">
              Data Source{selectedDataSources.length > 1 ? 's' : ''}
            </div>
            <div className="text-xs font-semibold text-secondary-900 dark:text-secondary-100 leading-tight">
              {selectedDataSources.length === 0
                ? 'None'
                : selectedDataSources.length === 1
                ? selectedDataSources[0].name
                : `${selectedDataSources.length} Selected`}
            </div>
          </div>
          {selectedDataSources.length > 1 && (
            <span className="px-1.5 py-0.5 bg-primary-600 text-white text-[10px] font-bold rounded-full">
              {selectedDataSources.length}
            </span>
          )}
          <ChevronDown className="w-3.5 h-3.5 text-secondary-400" />
        </button>
      )}

      {/* Non-Super Admin Company Badge (read-only) */}
      {!isSuperAdmin && userCompany && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-300 dark:border-green-600 rounded-lg shadow-sm">
          <Building2 className="w-4 h-4 text-green-600 dark:text-green-400" />
          <div className="text-left">
            <div className="text-[10px] font-medium text-green-600 dark:text-green-400 uppercase tracking-wider leading-tight">
              Your Company
            </div>
            <div className="text-xs font-semibold text-secondary-900 dark:text-secondary-100 leading-tight">
              {userCompany.name}
            </div>
          </div>
        </div>
      )}

      {/* Data Source Modal */}
      {dataSourceModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-center justify-center animate-fade-in p-4">
          <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden animate-scale-in">
            {/* Modal Header */}
            <div className="p-6 border-b border-secondary-200 dark:border-secondary-700">
              <h3 className="text-xl font-bold text-secondary-900 dark:text-white flex items-center gap-2">
                <Database className="w-6 h-6 text-primary-600" />
                Select Data Sources (Multi-Select)
              </h3>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                Select one or more data sources for{' '}
                <span className="font-semibold">{selectedCompany?.name}</span>
              </p>
              {selectedDataSources.length > 1 && (
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-blue-900 dark:text-blue-200 font-medium mb-1">
                    📊 Combined Module Coverage:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {combinedModules.map(module => (
                      <span
                        key={module}
                        className={`px-2 py-0.5 text-[10px] rounded ${
                          overlappingModules.includes(module)
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 font-semibold'
                            : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                        }`}
                        title={
                          overlappingModules.includes(module)
                            ? 'Data will be combined from multiple sources'
                            : 'Available from one source'
                        }
                      >
                        {module} {overlappingModules.includes(module) ? '🔗' : ''}
                      </span>
                    ))}
                  </div>
                  {overlappingModules.length > 0 && (
                    <p className="text-[10px] text-blue-700 dark:text-blue-300 mt-2">
                      🔗 = Data will be combined from {selectedDataSources.length} sources
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Data Source List - Multi-Select with Checkboxes */}
            <div className="p-4 sm:p-6 space-y-3 flex-1 overflow-y-auto scrollbar-thin">
              {availableDataSources
                .filter(ds => ds.isActive)
                .map(dataSource => {
                  const isSelected = selectedDataSources.find(ds => ds.id === dataSource.id);
                  return (
                    <button
                      key={dataSource.id}
                      onClick={() => handleDataSourceToggle(dataSource)}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                        isSelected
                          ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-secondary-300 dark:border-secondary-600 hover:border-primary-400 hover:bg-secondary-50 dark:hover:bg-secondary-700'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Checkbox */}
                        <div className="flex-shrink-0 mt-1">
                          <div
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                              isSelected
                                ? 'bg-primary-600 border-primary-600'
                                : 'bg-white dark:bg-secondary-700 border-secondary-400'
                            }`}
                          >
                            {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-secondary-900 dark:text-white">
                              {dataSource.name}
                            </h4>
                            {dataSource.isDefault && (
                              <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs rounded-full">
                                Default
                              </span>
                            )}
                          </div>
                          <div className="mt-2 space-y-1">
                            {dataSource.purpose && (
                              <div className="text-xs text-secondary-600 dark:text-secondary-400 italic mb-2">
                                {dataSource.purpose}
                              </div>
                            )}
                            <div className="text-xs text-secondary-600 dark:text-secondary-400">
                              <span className="font-medium">Type:</span>{' '}
                              {dataSource.sourceType === 'database'
                                ? '🗄️ Database'
                                : dataSource.sourceType === 'file_upload'
                                ? '📁 File Upload'
                                : dataSource.sourceType === 'api'
                                ? '🔌 API'
                                : dataSource.type.toUpperCase()}
                            </div>
                            <div className="text-xs text-secondary-600 dark:text-secondary-400">
                              <span className="font-medium">Database:</span>{' '}
                              {dataSource.config.database}
                              {dataSource.config.collection && ` / ${dataSource.config.collection}`}
                            </div>
                            {dataSource.availableModules &&
                              dataSource.availableModules.length > 0 && (
                                <div className="text-xs text-secondary-600 dark:text-secondary-400">
                                  <span className="font-medium">Available for:</span>{' '}
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {dataSource.availableModules.map(module => {
                                      const isOverlapping =
                                        selectedDataSources.length > 1 &&
                                        isSelected &&
                                        overlappingModules.includes(module);
                                      return (
                                        <span
                                          key={module}
                                          className={`px-1.5 py-0.5 rounded text-[10px] ${
                                            isOverlapping
                                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 font-semibold'
                                              : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300'
                                          }`}
                                          title={
                                            isOverlapping
                                              ? 'This module will combine data from multiple sources'
                                              : module
                                          }
                                        >
                                          {module} {isOverlapping ? '🔗' : ''}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            {dataSource.sourceType === 'file_upload' &&
                              dataSource.config.uploadedFiles &&
                              dataSource.config.uploadedFiles.length > 0 && (
                                <div className="text-xs text-secondary-600 dark:text-secondary-400">
                                  <span className="font-medium">Files:</span>{' '}
                                  {dataSource.config.uploadedFiles.length} uploaded
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-secondary-200 dark:border-secondary-700 flex items-center justify-between">
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                {selectedDataSources.length === 0 ? (
                  <span className="text-orange-600 dark:text-orange-400 font-medium">
                    ⚠️ Select at least one data source
                  </span>
                ) : (
                  <span className="font-medium">
                    {selectedDataSources.length} source{selectedDataSources.length > 1 ? 's' : ''}{' '}
                    selected
                  </span>
                )}
              </div>
              <button
                onClick={() => setDataSourceModalOpen(false)}
                className="btn btn-primary"
                disabled={selectedDataSources.length === 0}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDataSourceSelector;
